import React, {useState} from 'react';
import {MaterialCommunityIcons} from '@expo/vector-icons';
// import useUpsertUserLevel from '@/Hooks/RenewalApi/useUpsertUserLevel';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import {FontSizes, isPhone} from '@/Utils/Dimensions';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

interface Props {
  userId: string | undefined;
  refetchPricing: (params: {level: string; user_level_source: string}) => void;
  setSubmitting: (v: boolean) => void;
}

const levelOptions = [
  {
    id: 'Beginner',
    title: `I'm just starting out`,
    desc: `I'm learning how pieces move and basic rules`,
    icon: 'chess-pawn',
    color: updateColorWithOpacity(CCColors.Green, 0.15),
  },
  {
    id: 'Foundation',
    title: 'Have good hold on basics',
    desc: 'I know how to play a complete chess game and also know some tactics',
    icon: 'chess-knight',
    color: updateColorWithOpacity(CCColors.Blue, 0.15),
  },
  {
    id: 'Intermediate',
    title: 'I play online regularly',
    desc: 'I have tried offline tournaments too',
    icon: 'chess-rook',
    color: updateColorWithOpacity(CCColors.Purple, 0.15),
  },
  {
    id: 'Advanced',
    title: `I'm a competitive player`,
    desc: 'I have a FIDE rating',
    icon: 'chess-king',
    color: updateColorWithOpacity(CCColors.Orange, 0.15),
  },
];

const UserLevelRequiredPage = ({
  userId,
  refetchPricing,
  setSubmitting,
}: Props) => {
  const {isMobileOrTab} = useMediaQuery();
  const [selected, setSelected] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // const {mutate, isLoading} = useUpsertUserLevel();

  const isDisabled = !selected;

  const handleSubmit = () => {
    setErrorMsg(null);
    if (!selected) {
      setErrorMsg('Please select a level');
      return;
    }

    setSubmitting(true);
    refetchPricing({
      level: selected,
      user_level_source: 'user_preference',
    });

    // mutate(
    //   {
    //     user_id: userId,
    //     level: selected,
    //   },
    //   {
    //     onSuccess: () => {
    //       setSubmitting(true);
    //       refetchPricing();
    //     },
    //     onError: (err: any) => {
    //       setErrorMsg(
    //         err?.response?.data?.message ||
    //           err?.message ||
    //           'Failed to submit level',
    //       );
    //     },
    //   },
    // );
  };

  return (
    <Box style={styles.page}>
      <Box style={styles.bgContainer}>
        {bgPieces.map((p, i) => (
          <MaterialCommunityIcons
            key={i}
            name={p?.icon}
            size={p?.size}
            color={updateColorWithOpacity(CCColors?.Primary?.Brown, 0.05)}
            style={[
              {
                position: 'absolute',
                top: p?.top,
                left: p?.left,
                transform: [{rotate: p.rotate}],
              },
            ]}
          />
        ))}
      </Box>

      <Box style={styles.header}>
        <CCText
          fontSize={FontSizes[isMobileOrTab ? 18 : 32]}
          style={styles.title}>
          Lets find where you are in your chess learning journey
        </CCText>

        <CCText
          fontSize={FontSizes[16]}
          color={CCColors?.TextColor?.GreyText}
          style={styles?.subtitle}>
          Choose the level that best describes you
        </CCText>
      </Box>

      <Box style={styles?.card}>
        {levelOptions.map(item => {
          const active = selected === item?.id;

          return (
            <Box
              key={item?.id}
              onPress={() => setSelected(item?.id)}
              style={[
                styles.option,
                {
                  backgroundColor: item?.color,
                  borderColor: active ? CCColors?.Red : CCColors?.BorderLight,
                },
              ]}>
              <Box style={styles.iconBox}>
                <MaterialCommunityIcons
                  name={item.icon}
                  size={30}
                  color={CCColors.Primary.Brown}
                />
              </Box>
              <Box style={{flex: 1}}>
                <CCText
                  fontSize={FontSizes[18]}
                  color={CCColors?.TextColor?.Primary}
                  style={styles.optionTitle}>
                  {item?.title}
                </CCText>
                <CCText
                  fontSize={FontSizes[14]}
                  color={CCColors?.TextColor?.GreyText}>
                  {item?.desc}
                </CCText>
              </Box>
            </Box>
          );
        })}

        {errorMsg && (
          <CCText
            color={CCColors.Red}
            fontSize={FontSizes[14]}
            style={{marginBottom: 10}}>
            {errorMsg}
          </CCText>
        )}

        <CCButton
          style={styles.button}
          titleStyle={{fontSize: FontSizes[12]}}
          contentContainerStyle={{
            paddingVertical: 14,
            backgroundColor: isDisabled
              ? updateColorWithOpacity(CCColors?.Red, 0.4)
              : CCColors?.Red,
          }}
          onPress={handleSubmit}
          disabled={isDisabled}>
          Continue to Your Learning Path
        </CCButton>
      </Box>
    </Box>
  );
};

export default UserLevelRequiredPage;

const bgPieces = [
  {icon: 'chess-king', size: 220, top: '5%', left: '5%', rotate: '15deg'},
  {icon: 'chess-queen', size: 180, top: '10%', left: '30%', rotate: '-10deg'},
  {icon: 'chess-rook', size: 200, top: '20%', left: '70%', rotate: '25deg'},
  {icon: 'chess-knight', size: 160, top: '35%', left: '10%', rotate: '-20deg'},
  {icon: 'chess-pawn', size: 140, top: '40%', left: '50%', rotate: '10deg'},
  {icon: 'chess-king', size: 160, top: '55%', left: '80%', rotate: '-15deg'},
  {icon: 'chess-queen', size: 140, top: '65%', left: '20%', rotate: '20deg'},
  {icon: 'chess-rook', size: 180, top: '75%', left: '60%', rotate: '-25deg'},
  {icon: 'chess-knight', size: 170, top: '85%', left: '35%', rotate: '30deg'},
  {icon: 'chess-pawn', size: 150, top: '90%', left: '85%', rotate: '-30deg'},
  {icon: 'chess-queen', size: 130, top: '15%', left: '85%', rotate: '12deg'},
  {icon: 'chess-rook', size: 150, top: '50%', left: '5%', rotate: '-18deg'},
  {icon: 'chess-knight', size: 140, top: '70%', left: '45%', rotate: '22deg'},
  {icon: 'chess-pawn', size: 120, top: '30%', left: '90%', rotate: '-12deg'},
];

const styles: any = {
  page: {
    backgroundColor: CCColors?.Dashboard?.DashboardBackground,
    minHeight: '100vh',
    alignItems: 'center',
    paddingTop: 20,
    paddingHorizontal: 10,
  },
  bgContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  header: {
    width: isPhone ? '100%' : 650,
    marginBottom: 20,
  },
  title: {
    fontWeight: '700',
    lineHeight: isPhone ? 20 : 40,
  },
  subtitle: {
    marginTop: 8,
  },
  card: {
    width: isPhone ? '100%' : 650,
    padding: isPhone ? 16 : 24,
    borderRadius: 18,
    backgroundColor: CCColors?.White,
    borderWidth: 1,
    borderColor: CCColors?.BorderLight,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: isPhone ? 12 : 18,
    borderRadius: 12,
    borderWidth: 2,
    marginBottom: 12,
  },
  iconBox: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
    backgroundColor: updateColorWithOpacity(CCColors?.Primary?.Default, 0.2),
  },
  optionTitle: {
    fontWeight: '600',
  },
  button: {
    marginTop: 20,
  },
};
